import { Card, CardBody, Col, Form, FormGroup, Label, Row, Table } from 'reactstrap';
import React, { useEffect, useState } from 'react';

import { Button } from '../../components';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { DegreeByAuspiceReportDto } from '../../common/Types';
import { reportService } from '../../services/ReportService';
import MultiSelect from 'react-multi-select-component';
import styled from 'styled-components';
import { institutionService } from '../../services/InstitutionService';
import { DegreeType } from '../../common/Data';

interface AuspiceOption {
    label: string;
    value: number;
}

const StyledMultiSelect = styled(MultiSelect)`
    width: 300px;

    & .dropdown-container {
        width: 100%;
        max-width: 100%;
    }
    & .dropdown-content {
        z-index: 3;
        border: none !important;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
        
        /* Remove borders from ALL nested elements */
        *, & > *, & * {
            border: none !important;
            outline: none !important;
            box-shadow: none !important;
        }
        
        /* Force all child elements to have minimal left padding */
        & > * {
            padding-left: 0.25rem !important;
        }
        
        /* Position checkboxes properly to the left */
        input, [role="checkbox"], .checkbox, span, div {
            margin-left: -20px !important;
            margin-right: 0.5rem !important;
        }
    }
    & .select-item {
        display: flex !important;
        align-items: center !important;
        padding-left: 0.125rem !important;
        padding-right: 0.5rem !important;
        
        /* Ensure selection background spans full width */
        &.selected {
            background-color: rgba(13, 110, 253, 0.1) !important;
            width: 100% !important;
            margin-left: 0 !important;
            padding-left: 0.25rem !important;
        }
        
        /* Override the negative margins for selected items to fill width */
        &.selected input, &.selected [role="checkbox"], &.selected .checkbox, 
        &.selected span, &.selected div {
            margin-left: 0 !important;
        }
    }
`;
const degreeOptions = Object.entries(DegreeType)
    .filter((d) => !isNaN(d[0] as any))
    .map((d) => {
        return {
            label: String(d[1]),
            value: String(d[0]),
        };
    });
const DegreesByAuspiceReport = () => {
    const [selectedDegreeType, setSelectedDegreeType] = useState(degreeOptions);
    const [degreeByAuspiceReport, setDegreeByAuspiceReport] = useState<DegreeByAuspiceReportDto>();
    const [auspiceOptions, setAuspiceOptions] = useState<AuspiceOption[]>();
    const [selectedAuspices, setSelectedAuspices] = useState<AuspiceOption[]>();

    useEffect(() => {
        const loadAuspices = () => {
            institutionService
                .getAuspice()
                .then((results) => {
                    const mappedAuspices = (
                        results.filter((r) => r.isActive).sort((a, b) => a.title.localeCompare(b.title)) || []
                    ).map((a) => {
                        return {
                            label: String(a.title),
                            value: a.auspiceID,
                        };
                    });
                    setAuspiceOptions(mappedAuspices);
                    setSelectedAuspices(mappedAuspices);
                })
                .catch(() => {
                    toast.error('Error loading auspices');
                });
        };
        loadAuspices();
    }, [setAuspiceOptions]);

    const { handleSubmit } = useForm();

    const onSubmit = () => {
        const toastId = toast.info('Searching...', {
            autoClose: false,
        });
        reportService
            .getDegreesByAuspiceReport(
                selectedDegreeType?.map((d) => +d['value']),
                selectedAuspices?.map((a) => a['value']),
            )
            .then((response) => {
                setDegreeByAuspiceReport(response);
                toast.update(toastId, {
                    type: 'success',
                    autoClose: 500,
                    closeOnClick: true,
                    draggable: true,
                });
            })
            .catch(() => toast.error('Failed to generate report'));
    };

    const exportReport = () => {
        const toastId = toast.info('Generating report, this may take a minute...', {
            autoClose: false,
        });
        reportService
            .exportDegreesByAuspiceReport(
                selectedDegreeType?.map((d) => +d['value']),
                selectedAuspices?.map((a) => a['value']),
            )
            .then((report) => {
                if (report) {
                    const url = window.URL.createObjectURL(report);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `DegreesByAuspiceReport.xlsx`;
                    a.click();
                    toast.update(toastId, {
                        render: 'Report generated successfully.',
                        type: 'success',
                        autoClose: 2500,
                        closeOnClick: true,
                        draggable: true,
                    });
                }
            })
            .catch(() => {
                toast.error(`Unable to generate report`);
            });
    };

    return (
        <>
            <Card>
                <CardBody>
                    <Row>
                        <Col>
                            <div className="mb-3">
                                <h3>Degrees By Auspice Report</h3>
                            </div>
                            <Form noValidate onSubmit={handleSubmit(onSubmit)} className="d-flex align-items-center flex-wrap" style={{ gap: '1rem' }}>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="degreeTypes" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Degree Types
                                    </Label>
                                    <StyledMultiSelect
                                        options={degreeOptions}
                                        disableSearch={true}
                                        hasSelectAll={true}
                                        value={selectedDegreeType}
                                        onChange={setSelectedDegreeType}
                                        labelledBy="Degree Types"
                                    />
                                </div>
                                {auspiceOptions && auspiceOptions?.length > 0 && (
                                    <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                        <Label for="auspice" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                            Auspice
                                        </Label>
                                        <StyledMultiSelect
                                            options={auspiceOptions || []}
                                            disableSearch={true}
                                            hasSelectAll={true}
                                            value={selectedAuspices || []}
                                            onChange={setSelectedAuspices}
                                            labelledBy="Auspice"
                                        />
                                    </div>
                                )}
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Button type="submit">Search</Button>
                                </div>
                            </Form>
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end mt-2">
                        <Button type="button" color="info" onClick={() => exportReport()}>
                            Export Report
                        </Button>
                    </div>
                    <div className="mt-3">
                        <h4>Degrees By Auspices</h4>
                    </div>
                </CardBody>
                <Table>
                    {degreeByAuspiceReport?.degreeByAuspiceDtos ? (
                        degreeByAuspiceReport.degreeByAuspiceDtos.map((record) => (
                            <tbody key={record.degreeTypeId}>
                                <tr>
                                    <td className={`font-weight-bold`}>
                                        {record.degreeTypeTitle} - {record.totalCount} programs of which
                                    </td>
                                </tr>
                                {record.programByAuspiceDtos ? (
                                    record.programByAuspiceDtos.map((program) => (
                                        <div key={program.auspiceId}>
                                            <tr>
                                                <td>
                                                    {program.auspiceCount} are {program.auspiceTitle}
                                                </td>
                                            </tr>
                                        </div>
                                    ))
                                ) : (
                                    <tr>
                                        <td />
                                    </tr>
                                )}
                            </tbody>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={1}>No Results</td>
                        </tr>
                    )}
                    <tr>
                        <td className={`font-weight-bold`}>
                            Total Records: {degreeByAuspiceReport?.recordCount ? degreeByAuspiceReport?.recordCount : 0}
                        </td>
                    </tr>
                </Table>
            </Card>
        </>
    );
};

export default DegreesByAuspiceReport;
